'use client';

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Stack,
  Divider,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
  Link as MuiLink,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { Icon } from '@iconify/react';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import { computeAudit, type AuditResult, type AuditFinding, type AuditSnapshot } from '@/lib/auditEngine';
import { useAiActionCooldown } from '@/hooks/useAiActionCooldown';

function AuditScorecard({
  score,
  historyScores,
  ariaLabel = 'Audit score',
}: {
  score: number;
  historyScores: number[];
  ariaLabel?: string;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const spark = useMemo(() => {
    const arr = [...historyScores, score].slice(-12);
    const max = Math.max(...arr, 1);
    const w = isMobile ? 200 : 280;
    const h = 48;
    const pad = 8;
    const points = arr.map((v, i) => {
      const x = pad + (i / Math.max(1, arr.length - 1)) * (w - pad * 2);
      const y = h - pad - (v / max) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');
    return { w, h, points };
  }, [historyScores, score, isMobile]);

  return (
    <Paper component="section" aria-labelledby="audit-score-heading" sx={{ p: 2 }}>
      <Typography id="audit-score-heading" variant="h6" gutterBottom>
        Audit Score
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box
          role="img"
          aria-label={ariaLabel}
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: score >= 70 ? 'success.light' : score >= 40 ? 'warning.light' : 'error.light',
            color: 'success.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '1.5rem',
          }}
        >
          {score}
        </Box>
        {spark.points && (
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">Recent trend</Typography>
            <svg width={spark.w} height={spark.h} aria-hidden="true">
              <polyline
                points={spark.points}
                fill="none"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function FindingCard({ finding, handle, platform, session, plan, onAddToTasks }: { finding: AuditFinding; handle: string; platform: string; session?: any; plan?: string; onAddToTasks?: () => void }) {
  const auditAi = useAiActionCooldown(3000);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'explain' | 'suggest' | null>(null);
  const [modalContent, setModalContent] = useState<string>('');
  const [suggestionPayload, setSuggestionPayload] = useState<{ caption: string; hashtags: string[] } | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const aiDisabled = auditAi.disabled;

  const tryFixHref = useMemo(() => {
    if (finding.tryFixTool === 'calculator') {
      const params = new URLSearchParams(finding.tryFixQuery || '');
      return `/tools/social-media-calculator?${params.toString()}`;
    }
    if (finding.tryFixTool === 'hashtag-research' && finding.tryFixQuery) {
      return `/tools/hashtag-research?query=${encodeURIComponent(finding.tryFixQuery)}`;
    }
    return null;
  }, [finding]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalMode(null);
    setModalContent('');
    setSuggestionPayload(null);
  };

  const onExplain = () => {
    void auditAi.run(async () => {
      setContentLoading(true);
      setModalMode('explain');
      setModalContent('');
      setModalOpen(true);
      try {
        const res = await fetch('/api/tools/social-audit/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'explain', finding, snapshot: { handle, platform } }),
        });
        const j = await res.json();
        if (j.ok) setModalContent(j.result?.explanation || j.result?.raw || 'No explanation returned.');
        else setModalContent(j.error || 'AI request failed.');
      } catch (e) {
        console.error(e);
        setModalContent('Request error.');
      } finally {
        setContentLoading(false);
      }
    });
  };

  const onSuggest = () => {
    void auditAi.run(async () => {
      setContentLoading(true);
      setModalMode('suggest');
      setModalContent('');
      setSuggestionPayload(null);
      setModalOpen(true);
      try {
        const res = await fetch('/api/tools/social-audit/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'suggest', finding, snapshot: { handle, platform } }),
        });
        const j = await res.json();
        if (!j.ok) {
          setModalContent(j.error || 'AI request failed.');
          return;
        }
        const suggestions = j.result?.suggestions || [];
        if (!suggestions.length) {
          setModalContent('No suggestions returned.');
          return;
        }
        const first = suggestions[0];
        setSuggestionPayload({ caption: first.caption || '', hashtags: first.hashtags || [] });
        setModalContent(first.caption || '');
      } catch (e) {
        console.error(e);
        setModalContent('Request error.');
      } finally {
        setContentLoading(false);
      }
    });
  };

  const onAcceptSuggestion = async () => {
    if (!suggestionPayload) return;
    const userId = session?.user?.id || 'dev-user';
    await fetch('/api/tools/social-audit/ai/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, findingId: finding.id, caption: suggestionPayload.caption, hashtags: suggestionPayload.hashtags }),
    });
    const text = [suggestionPayload.caption, (suggestionPayload.hashtags || []).join(' ')].filter(Boolean).join('\n\n');
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); } catch {}
    }
    handleCloseModal();
  };

  return (
    <>
      <Paper component="article" sx={{ p: 2 }} aria-labelledby={`finding-${finding.id}-title`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography id={`finding-${finding.id}-title`} variant="subtitle1" component="h3">
            {finding.title}
          </Typography>
          <Chip size="small" label={finding.severity} color={finding.severity === 'high' ? 'error' : finding.severity === 'medium' ? 'warning' : 'default'} />
          <Chip size="small" variant="outlined" label={`Impact: ${finding.impact}`} />
          <Chip size="small" variant="outlined" label={`Difficulty: ${finding.difficulty}`} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {finding.details}
        </Typography>
        {finding.evidence.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">Evidence:</Typography>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
              {finding.evidence.map((e, i) => (
                <li key={i}><Typography variant="body2" component="span">{e}</Typography></li>
              ))}
            </ul>
          </Box>
        )}
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Remediation:</strong> {finding.remediation}
        </Typography>
        {tryFixHref && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" component={Link} href={tryFixHref} variant="outlined" aria-label={`Try fix: ${finding.title}`}>
              Try Fix
            </Button>
            <Button size="small" variant="outlined" onClick={onExplain} disabled={aiDisabled} aria-label="Explain this finding">
              Explain
            </Button>
            <Button size="small" variant="outlined" onClick={onSuggest} disabled={aiDisabled} aria-label="Suggest caption and hashtags">
              Suggest
            </Button>
            {session?.user && onAddToTasks && (
              <Button size="small" variant="outlined" onClick={onAddToTasks} aria-label="Add to my remediation tasks">
                Add to my tasks
              </Button>
            )}
            <Button size="small" onClick={async () => {
              try {
                const res = await fetch('/api/tools/social-audit/simulate', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ handle, adjustments: { engagementRateDelta: 0.2, postsPerWeekDelta: 1 } }),
                });
                const j = await res.json();
                if (j.ok) {
                  setModalMode(null);
                  setModalContent(`Projected monthly revenue: current $${j.base.monthlyRevenue.toFixed(2)} → modified $${j.modified.monthlyRevenue.toFixed(2)}`);
                  setSuggestionPayload(null);
                  setModalOpen(true);
                }
              } catch (e) {
                console.error(e);
              }
            }} variant="outlined">Simulate impact</Button>
          </Box>
        )}
      </Paper>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth aria-labelledby="ai-modal-title">
        <DialogTitle id="ai-modal-title">
          {modalMode === 'explain' ? 'AI explanation' : modalMode === 'suggest' ? 'Caption suggestion' : 'Result'}
        </DialogTitle>
        <DialogContent>
          {contentLoading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{modalContent}</Typography>
              {modalMode === 'suggest' && suggestionPayload && suggestionPayload.hashtags?.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Hashtags: {suggestionPayload.hashtags.join(' ')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {modalMode === 'suggest' && suggestionPayload && (
            <>
              <Button onClick={onAcceptSuggestion} color="primary" variant="contained">Accept & copy</Button>
              {plan && ['CREATOR', 'PRO', 'ENTERPRISE', 'WHITE_LABEL'].includes(plan) && (
                <Button
                  onClick={async () => {
                    if (!suggestionPayload) return;
                    try {
                      const res = await fetch('/api/tools/social-audit/schedule-post', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ caption: suggestionPayload.caption, hashtags: suggestionPayload.hashtags, handle, platform }),
                      });
                      const j = await res.json();
                      if (j.ok) handleCloseModal();
                      else setModalContent((c) => `${c}\n\nSchedule failed: ${j.error || 'Unknown'}`);
                    } catch (e) {
                      setModalContent((c) => `${c}\n\nSchedule error.`);
                    }
                  }}
                  variant="outlined"
                >
                  Schedule this post
                </Button>
              )}
            </>
          )}
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function buildAuditCSV(snapshot: AuditSnapshot | null, audit: AuditResult | null): string {
  if (!audit) return '';
  const rows = [
    ['Handle', 'Platform', 'Score', 'Engagement Rate', 'Followers', 'Posts/Week'],
    [snapshot?.handle ?? '', snapshot?.platform ?? '', String(audit.score), `${(audit.metrics.engagementRate * 100).toFixed(2)}%`, String(audit.metrics.followers), String(audit.metrics.postsPerWeek)],
    [],
    ['Finding', 'Severity', 'Details', 'Remediation', 'Impact', 'Difficulty'],
    ...audit.findings.map((f) => [f.title, f.severity, f.details, f.remediation, f.impact, f.difficulty]),
  ];
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
}

export default function SocialMediaAuditPage() {
  const { data: session } = useSession();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const plan = (session?.user?.plan as string) || 'FREE';
  const canUnlink = plan !== 'FREE';

  const [handle, setHandle] = useState('example');
  const [platform, setPlatform] = useState('instagram');
  const [snapshot, setSnapshot] = useState<AuditSnapshot | null>(null);
  const [audit, setAudit] = useState<AuditResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [auditStatus, setAuditStatus] = useState('');

  const runAudit = async () => {
    setLoading(true);
    setAudit(null);
    setSnapshot(null);
    setAuditStatus('');
    try {
      const res = await fetch('/api/tools/social-audit/snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, platform }),
      });
      const json = await res.json();
      if (json.ok) {
        setSnapshot(json.snapshot);
        setAudit(json.audit);
        setAuditStatus(`Audit complete — score ${json.audit.score}`);
        try {
          await fetch('/api/tools/social-audit/save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ handle, snapshot: json.snapshot }),
          });
        } catch {}
        fetchHistory();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openPrintable = () => {
    const html = `<html><head><title>Audit - ${handle}</title></head><body><h1>Audit: ${handle}</h1><pre>${JSON.stringify({ snapshot, audit }, null, 2)}</pre></body></html>`;
    const w = window.open('', '_blank', 'noopener');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };

  const downloadCSV = () => {
    const csv = buildAuditCSV(snapshot, audit);
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${handle}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printOrSavePdf = () => {
    if (!audit || !snapshot) return;
    (async () => {
      try {
        const { jsPDF } = await import('jspdf');
        // Convert SVG logo to PNG data URL for embedding
        async function svgToPngDataUrl(svgUrl: string, width = 160, height = 48): Promise<string | null> {
          try {
            const svgText = await fetch(svgUrl).then((r) => r.text());
            const svg = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svg);
            const img = new Image();
            img.src = url;
            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error('SVG load error'));
            });
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { URL.revokeObjectURL(url); return null; }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/png');
            URL.revokeObjectURL(url);
            return dataUrl;
          } catch (err) {
            console.warn('svgToPngDataUrl failed', err);
            return null;
          }
        }

        const doc = new jsPDF({ unit: 'pt', format: 'letter' });
        const margin = 40;
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - margin * 2;
        let y = margin;

        const logoData = await svgToPngDataUrl('/floai-studio.svg', 160, 48);
        if (logoData) {
          doc.addImage(logoData, 'PNG', margin, y, 120, 36);
        } else {
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.text('Floai Studio', margin, y + 18);
          doc.setFont(undefined, 'normal');
        }
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Social Media Audit Report`, margin + (logoData ? 140 : 0), y + 18);
        doc.setFont(undefined, 'normal');
        y += 56;

        doc.setFontSize(10);
        doc.text(`Handle: ${handle}`, margin, y);
        doc.text(`Platform: ${platform}`, margin + usableWidth / 3, y);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, margin + (usableWidth / 3) * 2, y);
        y += 18;

        doc.setFontSize(14);
        doc.text(`Score: ${audit.score}/100`, margin, y);
        y += 18;
        doc.setFontSize(11);
        doc.text(`Engagement rate: ${(audit.metrics.engagementRate * 100).toFixed(2)}%`, margin, y);
        doc.text(`Followers: ${audit.metrics.followers}`, margin + usableWidth / 2, y);
        y += 22;

        doc.setFontSize(12);
        doc.text('Top Findings', margin, y);
        y += 12;

        doc.setFontSize(10);
        for (const f of audit.findings) {
          const title = `${f.title} (${f.severity})`;
          const details = f.details || '';
          const remediation = `Remediation: ${f.remediation || ''}`;

          const titleSplit = doc.splitTextToSize(title, usableWidth);
          if (y + titleSplit.length * 12 > 740) { doc.addPage(); y = margin; }
          doc.setFont(undefined, 'bold');
          doc.text(titleSplit, margin, y);
          doc.setFont(undefined, 'normal');
          y += titleSplit.length * 12;

          const detailsSplit = doc.splitTextToSize(details, usableWidth);
          if (y + detailsSplit.length * 12 > 740) { doc.addPage(); y = margin; }
          doc.text(detailsSplit, margin, y);
          y += detailsSplit.length * 12 + 6;

          const remSplit = doc.splitTextToSize(remediation, usableWidth);
          if (y + remSplit.length * 12 > 740) { doc.addPage(); y = margin; }
          doc.setTextColor(80, 80, 80);
          doc.text(remSplit, margin + 8, y);
          doc.setTextColor(0, 0, 0);
          y += remSplit.length * 12 + 10;
        }

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(9);
          doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 20, { align: 'center' });
        }

        doc.save(`audit-${handle}-${new Date().toISOString().slice(0,10)}.pdf`);
      } catch (err) {
        console.error('PDF generation error', err);
      }
    })();
  };

  const [history, setHistory] = useState<{ score?: number; followers?: number; ts?: string }[]>([]);
  const [connectedAccounts, setConnectedAccounts] = useState<any[]>([]);
  const [compareA, setCompareA] = useState<number | null>(null);
  const [compareB, setCompareB] = useState<number | null>(null);
  const fetchHistory = async () => {
    try {
      const res = await fetch(`/api/tools/social-audit/history?handle=${encodeURIComponent(handle)}`);
      const j = await res.json();
      if (j.ok) setHistory(j.history || []);
    } catch (e) {
      console.error(e);
    }
  };

  const [cronExpr, setCronExpr] = useState('0 0 * * *');
  const schedule = async () => {
    try {
      await fetch('/api/tools/social-audit/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, cronExpression: cronExpr, user: 'admin' }),
      });
      alert('Scheduled');
      fetchSchedules();
    } catch (e) {
      console.error(e);
    }
  };

  const [schedules, setSchedules] = useState<{ handle: string; cronExpression?: string; user?: string; createdAt?: string }[]>([]);
  const fetchSchedules = async () => {
    try {
      const res = await fetch('/api/tools/social-audit/schedule/list');
      const j = await res.json();
      if (j.ok) setSchedules(j.schedules || []);
    } catch (e) {
      console.error(e);
    }
  };
  const [syncLogs, setSyncLogs] = useState<any[]>([]);
  const fetchSyncLogs = async () => {
    try {
      const res = await fetch('/api/tools/social-audit/sync/logs');
      const j = await res.json();
      if (j.ok) setSyncLogs(j.logs || []);
    } catch (e) {
      console.error(e);
    }
  };
  const [errorsList, setErrorsList] = useState<any[]>([]);
  const fetchErrors = async () => {
    try {
      const res = await fetch('/api/tools/social-audit/errors');
      const j = await res.json();
      if (j.ok) setErrorsList(j.errors || []);
    } catch (e) {
      console.error(e);
    }
  };

  const [remediationTasks, setRemediationTasks] = useState<{ id: string; findingTitle: string; handle: string; platform: string; status: string }[]>([]);
  const fetchRemediationTasks = async () => {
    try {
      const res = await fetch('/api/tools/social-audit/tasks');
      const j = await res.json();
      if (j.ok) setRemediationTasks(j.tasks || []);
    } catch (e) {
      console.error(e);
    }
  };
  React.useEffect(() => { if (session?.user) fetchRemediationTasks(); }, [session?.user]);

  const [rollupHandles, setRollupHandles] = useState('');
  const [rollupResult, setRollupResult] = useState<{ handles: { handle: string; avgScore: number; count: number }[]; aggregate: { avgScore: number; totalSnapshots: number } } | null>(null);
  const [rollupLoading, setRollupLoading] = useState(false);
  const fetchRollup = async () => {
    const handles = rollupHandles.split(',').map((h) => h.trim()).filter(Boolean);
    if (!handles.length) return;
    setRollupLoading(true);
    try {
      const res = await fetch(`/api/tools/social-audit/rollup?handles=${encodeURIComponent(handles.join(','))}`);
      const j = await res.json();
      if (j.ok) setRollupResult({ handles: j.handles, aggregate: j.aggregate });
      else setRollupResult(null);
    } catch (e) {
      setRollupResult(null);
    } finally {
      setRollupLoading(false);
    }
  };

  const deleteSchedule = async (h: string) => {
    try {
      const res = await fetch('/api/tools/social-audit/schedule/delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle: h }) });
      const j = await res.json();
      if (j.ok) {
        fetchSchedules();
      } else {
        alert('Delete failed');
      }
    } catch (e) {
      console.error(e);
      alert('Delete error');
    }
  };
  // Inline edit state
  const [editingHandle, setEditingHandle] = useState<string | null>(null);
  const [editingCron, setEditingCron] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  function isValidCron(expr: string) {
    if (!expr) return false;
    const parts = expr.trim().split(/\s+/);
    return parts.length === 5 || parts.length === 6;
  }

  const saveInline = async (handleKey: string) => {
    if (!isValidCron(editingCron)) {
      alert('Invalid cron expression. Use 5 fields (minute hour day month weekday).');
      return;
    }
    try {
      const res = await fetch('/api/tools/social-audit/schedule', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle: handleKey, cronExpression: editingCron, user: 'admin' }) });
      const j = await res.json();
      if (j.ok) {
        setEditingHandle(null);
        setEditingCron('');
        fetchSchedules();
      } else {
        alert('Save failed');
      }
    } catch (e) {
      console.error(e);
      alert('Save error');
    }
  };

  const purgeHistory = async (handleKey: string) => {
    try {
      const res = await fetch('/api/tools/social-audit/schedule/purge-history', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle: handleKey }) });
      const j = await res.json();
      if (j.ok) {
        alert('History purged');
        fetchHistory();
      } else {
        alert('Purge failed');
      }
    } catch (e) {
      console.error(e);
      alert('Purge error');
    }
  };

  const fetchConnected = async () => {
    try {
      const res = await fetch('/api/tools/social-audit/oauth/instagram/connected');
      const j = await res.json();
      if (j.ok) setConnectedAccounts(j.accounts || []);
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    fetchHistory();
    fetchConnected();
    fetchSchedules();
    fetchSyncLogs();
    fetchErrors();
  }, []);

  const historyScores = useMemo(() => {
    return history
      .map((h: any) => {
        if (typeof h.score === 'number') return h.score;
        try {
          const a = computeAudit(h);
          return a.score;
        } catch {
          return null;
        }
      })
      .filter((s): s is number => typeof s === 'number');
  }, [history]);

  // Dynamic chart for history
  const DynamicLineChart = React.lazy(() => import('@/components/charts/SimpleLineChart').then((m) => ({ default: m.default })));
  const historyChartData = history.map((h) => ({ date: h.ts || '', tokens: typeof h.score === 'number' ? h.score : 0 }));

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton component={Link} href="/tools" aria-label="Back to Tools">
          <ArrowBackIcon />
        </IconButton>
        <Icon icon={getToolIconForPath('/tools/social-media-audit')} width={34} height={34} aria-hidden />
        <Typography variant="h4">Social Media Audit</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            fullWidth
            inputProps={{ 'aria-label': 'Social media handle' }}
          />
          <TextField
            label="Platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            fullWidth
            inputProps={{ 'aria-label': 'Platform' }}
          />
          <Box sx={{ transform: { xs: 'translateX(-6px)', sm: 'none' } }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                onClick={runAudit}
                disabled={loading}
                aria-busy={loading}
                aria-label="Run audit for the entered handle and platform"
                sx={{
                  pl: 2,
                  pr: 2,
                  ml: 0,
                  minWidth: 140,
                  fontWeight: 600,
                  border: '2px solid',
                  borderColor: 'primary.dark',
                  boxShadow: 2,
                }}
              >
                {loading ? 'Running…' : 'Run Audit'}
              </Button>
              <Button
                variant="outlined"
                onClick={openPrintable}
                disabled={!audit}
                aria-label="Open report in new window"
                sx={{ pl: 0, ml: 0, minWidth: 'auto', px: 1 }}
              >
                Open Report
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Button
                sx={{ pl: 0, ml: 0, minWidth: 'auto', px: 1 }}
                variant="outlined"
                onClick={downloadCSV}
                disabled={!audit}
                aria-label="Download CSV"
              >
                Download CSV
              </Button>
              <Button
                sx={{ pl: 0, ml: 0, minWidth: 'auto', px: 1 }}
                variant="outlined"
                onClick={printOrSavePdf}
                disabled={!audit}
                aria-label="Print or save as PDF"
              >
                Print / Save as PDF
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {audit && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <AuditScorecard score={audit.score} historyScores={historyScores} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Engagement rate: {(audit.metrics.engagementRate * 100).toFixed(2)}% · Followers: {audit.metrics.followers} · Posts/week: {audit.metrics.postsPerWeek}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mt: 2 }} component="section" aria-labelledby="findings-heading">
            <Typography id="findings-heading" variant="h6" gutterBottom>
              Top Findings
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {audit.findings.map((f) => (
                <FindingCard
                  key={f.id}
                  finding={f}
                  handle={handle}
                  platform={platform}
                  session={session}
                  plan={plan}
                  onAddToTasks={session?.user ? async () => {
                    try {
                      await fetch('/api/tools/social-audit/tasks', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ findingId: f.id, findingTitle: f.title, handle, platform }),
                      });
                      fetchRemediationTasks();
                    } catch (e) { console.error(e); }
                  } : undefined}
                />
              ))}
            </Stack>
          </Paper>

          {session?.user && (
            <Paper sx={{ p: 2, mt: 2 }} component="section" aria-labelledby="remediation-tasks-heading">
              <Typography id="remediation-tasks-heading" variant="h6" gutterBottom>Remediation tasks</Typography>
              {remediationTasks.length === 0 ? (
                <Typography color="text.secondary">No tasks yet. Use &quot;Add to my tasks&quot; on a finding to track it.</Typography>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {remediationTasks.map((t) => (
                    <Paper key={t.id} sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="body2"><strong>{t.findingTitle}</strong></Typography>
                        <Typography variant="caption" color="text.secondary">{t.handle} · {t.platform}</Typography>
                      </Box>
                      <TextField
                        select
                        size="small"
                        label="Status"
                        value={t.status}
                        onChange={async (e) => {
                          const status = e.target.value as string;
                          try {
                            const res = await fetch(`/api/tools/social-audit/tasks/${encodeURIComponent(t.id)}`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ status }),
                            });
                            if ((await res.json()).ok) fetchRemediationTasks();
                          } catch (err) { console.error(err); }
                        }}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 140 }}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In progress</option>
                        <option value="done">Done</option>
                      </TextField>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          )}

          <Paper sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="h6">Snapshot</Typography>
              <Button size="small" component={Link} href="/api/tools/social-audit/oauth/instagram/start">
                Connect Instagram
              </Button>
            </Stack>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: 200 }}>{JSON.stringify(snapshot, null, 2)}</Box>
          </Paper>

          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Connected Accounts</Typography>
            {connectedAccounts.length > 0 ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {connectedAccounts.map((a: any) => (
                  <Paper key={a.key} sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2">{a.userId} — {a.accessTokenMasked}</Typography>
                    {canUnlink && (
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          try {
                            await fetch('/api/tools/social-audit/oauth/instagram/disconnect', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ userId: a.userId }),
                            });
                            fetchConnected();
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      >
                        Disconnect
                      </Button>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No connected Instagram accounts.</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">History</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button onClick={fetchHistory}>Refresh History</Button>
              <Button onClick={async () => {
                try {
                  const res = await fetch('/api/tools/social-audit/sync', { method: 'POST' });
                  const j = await res.json();
                  if (j.ok) {
                    alert('Sync completed');
                    fetchHistory();
                  } else {
                    alert('Sync failed: ' + (j.error || 'unknown'));
                  }
                } catch (e) {
                  console.error(e);
                  alert('Sync error');
                }
              }} variant="outlined">Sync all handles</Button>
            </Stack>
            <Box sx={{ mt: 1 }} component="section" aria-labelledby="audit-history-heading">
              <Typography id="audit-history-heading" variant="h6" gutterBottom>Audit history & timeline</Typography>
              {history.length > 0 ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Score over time</Typography>
                  <Box sx={{ mb: 2 }} role="img" aria-label="Audit score over time">
                    <React.Suspense fallback={<Typography color="text.secondary">Loading chart...</Typography>}>
                      {/* @ts-ignore */}
                      <DynamicLineChart data={historyChartData} />
                    </React.Suspense>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Recent sync runs</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', py: 1 }} role="list">
                      {syncLogs.slice(0, 12).map((l: any, i: number) => (
                        <Box key={i} role="listitem" sx={{ minWidth: 140, p: 1, borderRadius: 1, bgcolor: l.status === 200 || l.result === 'ok' ? 'success.light' : 'error.light' }}>
                          <Typography variant="caption">{new Date(l.ts).toLocaleString()}</Typography>
                          <Typography variant="body2">Duration: {l.durationMs}ms</Typography>
                          <Typography variant="caption" color="text.secondary">{l.result || l.status}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Compare snapshots</Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                      <Box sx={{ minWidth: 140 }}>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <TextField
                            select
                            size="small"
                            label="Snapshot A"
                            value={compareA ?? ''}
                            onChange={(e) => setCompareA(e.target.value === '' ? null : Number(e.target.value))}
                            SelectProps={{ native: true }}
                          >
                            <option value="">Select A</option>
                            {history.map((h, i) => <option key={i} value={i}>{h.ts || `Snapshot ${i+1}`}</option>)}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Snapshot B"
                            value={compareB ?? ''}
                            onChange={(e) => setCompareB(e.target.value === '' ? null : Number(e.target.value))}
                            SelectProps={{ native: true }}
                          >
                            <option value="">Select B</option>
                            {history.map((h, i) => <option key={i} value={i}>{h.ts || `Snapshot ${i+1}`}</option>)}
                          </TextField>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }} aria-label="History data">
                          {JSON.stringify(history.map((h) => ({ ts: h.ts, followers: h.followers, score: h.score })), null, 2)}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {compareA !== null && compareB !== null && history[compareA] && history[compareB] && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">Comparison</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 1 }} component="article" aria-label={`Snapshot A: ${history[compareA].ts || 'A'}`}>
                            <Typography variant="caption">Snapshot A ({history[compareA].ts || '—'})</Typography>
                            <Typography>Score: {history[compareA].score}</Typography>
                            <Typography>Followers: {history[compareA].followers}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 1 }} component="article" aria-label={`Snapshot B: ${history[compareB].ts || 'B'}`}>
                            <Typography variant="caption">Snapshot B ({history[compareB].ts || '—'})</Typography>
                            <Typography>Score: {history[compareB].score}</Typography>
                            <Typography>Followers: {history[compareB].followers}</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">No history yet. Run audit to save snapshots.</Typography>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Schedule automatic re-audit</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <TextField size="small" value={cronExpr} onChange={(e) => setCronExpr(e.target.value)} label="Cron" inputProps={{ 'aria-label': 'Cron expression' }} />
                <Button variant="outlined" onClick={schedule}>Schedule</Button>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Active schedules</Typography>
                {schedules.length === 0 && <Typography color="text.secondary" sx={{ mt: 1 }}>No schedules</Typography>}
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {schedules.map((s) => (
                    <Paper key={s.handle} sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2"><strong>{s.handle}</strong></Typography>
                        {editingHandle === s.handle ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField size="small" value={editingCron} onChange={(e) => setEditingCron(e.target.value)} />
                            <Button size="small" onClick={() => saveInline(s.handle)}>Save</Button>
                            <Button size="small" onClick={() => { setEditingHandle(null); setEditingCron(''); }}>Cancel</Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">{s.cronExpression || '—'}</Typography>
                        )}
                      </Box>
                      <Box>
                        <Button size="small" disabled={!canUnlink} onClick={() => { setEditingHandle(s.handle); setEditingCron(s.cronExpression || '0 0 * * *'); }}>Edit</Button>
                        <Button size="small" color="error" disabled={!canUnlink} onClick={() => { setConfirmAction(() => () => deleteSchedule(s.handle)); setConfirmOpen(true); }}>Remove</Button>
                        <Button size="small" color="warning" disabled={!canUnlink} onClick={() => { setConfirmAction(() => () => purgeHistory(s.handle)); setConfirmOpen(true); }}>Purge history</Button>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm</DialogTitle>
            <DialogContent>
              <Typography>Are you sure?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={() => { setConfirmOpen(false); confirmAction(); }} color="error">Confirm</Button>
            </DialogActions>
          </Dialog>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Recent sync runs</Typography>
                {syncLogs.length === 0 && <Typography color="text.secondary" sx={{ mt: 1 }}>No recent runs</Typography>}
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {syncLogs.slice(0, 6).map((l: any, i: number) => (
                    <Paper key={i} sx={{ p: 1 }}>
                      <Typography variant="caption" color="text.secondary">{l.ts}</Typography>
                      <Typography variant="body2">Duration: {l.durationMs}ms · Handles: {l.count}</Typography>
                      {l.resultsSummary && <Box component="pre" sx={{ maxHeight: 80, overflow: 'auto', mt: 1 }}>{JSON.stringify(l.resultsSummary, null, 2)}</Box>}
                    </Paper>
                  ))}
                </Stack>
              </Box>

              {(plan === 'CREATOR' || plan === 'PRO' || plan === 'ENTERPRISE' || plan === 'WHITE_LABEL') && (
                <Box sx={{ mt: 2 }} component="section" aria-labelledby="rollup-heading">
                  <Typography id="rollup-heading" variant="subtitle2">Team rollup (multi-account)</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" alignItems="center">
                    <TextField
                      size="small"
                      label="Handles (comma-separated)"
                      value={rollupHandles}
                      onChange={(e) => setRollupHandles(e.target.value)}
                      placeholder="handle1, handle2, handle3"
                      sx={{ minWidth: 260 }}
                      inputProps={{ 'aria-label': 'Handles for rollup' }}
                    />
                    <Button variant="outlined" onClick={fetchRollup} disabled={rollupLoading || !rollupHandles.trim()}>
                      {rollupLoading ? 'Loading…' : 'View rollup'}
                    </Button>
                  </Stack>
                  {rollupResult && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">Aggregate: avg score <strong>{rollupResult.aggregate.avgScore}</strong> · {rollupResult.aggregate.totalSnapshots} snapshots across {rollupResult.handles.length} handles</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                        {rollupResult.handles.map((h) => (
                          <Chip key={h.handle} size="small" label={`${h.handle}: ${h.avgScore} (${h.count})`} variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Recent errors</Typography>
                {errorsList.length === 0 && <Typography color="text.secondary" sx={{ mt: 1 }}>No recent errors</Typography>}
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {errorsList.slice(0, 8).map((e: any, i: number) => (
                    <Paper key={i} sx={{ p: 1 }}>
                      <Typography variant="caption" color="text.secondary">{e.ts}</Typography>
                      <Typography variant="body2">{e.message}</Typography>
                      {e.context && <Typography variant="caption" color="text.secondary">{JSON.stringify(e.context)}</Typography>}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
